/**
 * DOS518 - Digital Operations System for Wildfire Management
 * Copyright (c) 2024-2025 Angelo Irtilaç — All rights reserved.
 *
 * ReportFinale — Genera il verbale ufficiale dell'intervento AIB
 * Raccoglie automaticamente tutti i dati DOS518:
 *   - Scheda COAU
 *   - Personale impiegato
 *   - Mezzi terrestri e aerei
 *   - Droni (Linea 5B)
 *   - Diario operativo
 *   - L.A.C.E.S.
 * Genera un documento HTML ottimizzato per la stampa — apribile
 * nel browser del Samsung Tab Active5 Pro e salvabile come PDF
 * tramite "Stampa → Salva come PDF" (funzione nativa Android Chrome).
 */
// src/components/Report/ReportFinale.tsx
import React, { useState } from 'react';
import { useAppStore } from '../../store/useAppStore';

// ================================================================
// GENERATORE HTML REPORT
// ================================================================

function generaHTML(state: ReturnType<typeof useAppStore.getState>): string {
  const { coau, personale, mezziTerrestri, mezziAerei, droni, laces, diarioOperativo, dosAttivo, oraAttivazione } = state;
  const ora = new Date().toLocaleString('it-IT');
  const dataReport = new Date().toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });

  // Helper per righe tabella
  const tr = (cells: string[], header = false) => {
    const tag = header ? 'th' : 'td';
    return `<tr>${cells.map(c => `<${tag}>${c}</${tag}>`).join('')}</tr>`;
  };

  // ── COAU ──
  const coauHtml = coau ? `
    <table>
      ${tr(['Comune / Località', (coau as any).comune || (coau as any).localizzazione || '—'])}
      ${tr(['Coordinate', `${(coau as any).latitudine || '—'} , ${(coau as any).longitudine || '—'}`])}
      ${tr(['Codice intervento', (coau as any).codiceIntervento || '—'])}
      ${tr(['Data / Ora', (coau as any).data ? new Date((coau as any).data).toLocaleString('it-IT') : '—'])}
      ${(coau as any).tipo ? tr(['Tipo evento', (coau as any).tipo]) : ''}
      ${(coau as any).vegetazione ? tr(['Vegetazione', (coau as any).vegetazione]) : ''}
      ${(coau as any).orografia ? tr(['Orografia', (coau as any).orografia]) : ''}
      ${(coau as any).note ? tr(['Note', (coau as any).note]) : ''}
    </table>` : '<p class="empty">Scheda COAU non compilata</p>';

  // ── Personale ──
  const personaleHtml = personale.length > 0 ? `
    <table>
      <thead>${tr(['Cognome Nome', 'Qualifica', 'Telefono', 'Radio'], true)}</thead>
      <tbody>
        ${personale.map(p => tr([
          `${p.cognome || ''} ${p.nome || ''}`.trim() || '—',
          p.qualifica || '—',
          p.telefono || '—',
          (p as any).radio || '—',
        ])).join('')}
      </tbody>
    </table>` : '<p class="empty">Nessun operatore registrato</p>';

  // ── Mezzi Terrestri ──
  const mezziTHtml = mezziTerrestri.length > 0 ? `
    <table>
      <thead>${tr(['Sigla', 'Tipo', 'Targa', 'Ente', 'Stato'], true)}</thead>
      <tbody>
        ${mezziTerrestri.map(m => tr([
          m.sigla || '—',
          m.tipo || '—',
          (m as any).targa || '—',
          (m as any).ente || '—',
          (m as any).posizioneOperativa || '—',
        ])).join('')}
      </tbody>
    </table>` : '<p class="empty">Nessun mezzo terrestre registrato</p>';

  // ── Mezzi Aerei ──
  const mezziAHtml = mezziAerei.length > 0 ? `
    <table>
      <thead>${tr(['Sigla', 'Tipo', 'Modello', 'Stato'], true)}</thead>
      <tbody>
        ${mezziAerei.map(m => tr([
          m.sigla || '—',
          m.tipo || '—',
          (m as any).modello || '—',
          (m as any).posizioneOperativa || '—',
        ])).join('')}
      </tbody>
    </table>` : '<p class="empty">Nessun mezzo aereo registrato</p>';

  // ── Droni ──
  const droniHtml = droni.length > 0 ? `
    <table>
      <thead>${tr(['Modello/ID', 'Operatore', 'Missione', 'Coordinate rilevate', 'Ora'], true)}</thead>
      <tbody>
        ${droni.map(d => tr([
          (d as any).modello || d.operatore || '—',
          d.operatore || '—',
          (d as any).missione || '—',
          (d as any).coordsRilevate || '—',
          (d as any).oraRilevamento || '—',
        ])).join('')}
      </tbody>
    </table>` : '<p class="empty">Nessun drone registrato</p>';

  // ── Diario Operativo ──
  const diarioHtml = diarioOperativo && diarioOperativo.length > 0 ? `
    <table>
      <thead>${tr(['Ora', '', 'Evento', 'DOS'], true)}</thead>
      <tbody>
        ${diarioOperativo.map(v => tr([
          v.ora,
          v.icona,
          v.testo,
          v.dosAttivo || '—',
        ])).join('')}
      </tbody>
    </table>` : '<p class="empty">Diario operativo vuoto</p>';

  // ── Riepilogo numeri ──
  const totPersonale = personale.length;
  const totMezziT    = mezziTerrestri.length;
  const totMezziA    = mezziAerei.length;
  const totDroni     = droni.length;
  const totVoci      = (diarioOperativo || []).length;

  return `<!DOCTYPE html>
<html lang="it">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>DOS518 — Verbale Intervento AIB</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; font-size: 11pt; color: #111; background: #fff; }

  /* Intestazione */
  .header { background: #1a1a2e; color: #fff; padding: 18px 24px; display: flex; justify-content: space-between; align-items: flex-start; }
  .header-title { font-size: 1.4em; font-weight: 900; letter-spacing: 1px; }
  .header-subtitle { font-size: 0.85em; color: #aaa; margin-top: 4px; }
  .header-meta { text-align: right; font-size: 0.8em; color: #ccc; }
  .header-logo { font-size: 2.5em; line-height: 1; }

  /* Fascia riepilogo */
  .summary-bar { background: #f97316; color: #fff; padding: 10px 24px; display: flex; gap: 24px; flex-wrap: wrap; }
  .summary-item { text-align: center; }
  .summary-num { font-size: 1.6em; font-weight: 900; }
  .summary-label { font-size: 0.7em; text-transform: uppercase; letter-spacing: 0.5px; }

  /* Corpo */
  .body { padding: 16px 24px; }

  /* Sezione */
  .section { margin-bottom: 20px; break-inside: avoid; }
  .section-header { background: #1e3a5f; color: #fff; padding: 6px 12px; border-radius: 4px; font-size: 1em; font-weight: 700; margin-bottom: 8px; display: flex; justify-content: space-between; }
  .section-header .badge { background: rgba(255,255,255,0.2); border-radius: 10px; padding: 1px 8px; font-size: 0.8em; }

  /* Tabelle */
  table { width: 100%; border-collapse: collapse; font-size: 0.85em; }
  th { background: #f0f4f8; padding: 6px 8px; text-align: left; font-weight: 700; border: 1px solid #d1d5db; }
  td { padding: 5px 8px; border: 1px solid #e5e7eb; vertical-align: top; }
  tr:nth-child(even) td { background: #f9fafb; }
  tr:hover td { background: #eff6ff; }

  /* Diario */
  .diario-row-allerta td { background: #fff5f5 !important; color: #b91c1c; font-weight: 600; }
  .diario-row-cambio td { background: #fdf4ff !important; color: #7c3aed; font-weight: 700; }

  .empty { color: #9ca3af; font-style: italic; padding: 8px 0; }

  /* Firma */
  .firma-section { margin-top: 30px; border-top: 2px solid #f97316; padding-top: 16px; }
  .firma-box { border: 1px solid #d1d5db; border-radius: 6px; padding: 12px 16px; margin-top: 8px; }
  .firma-line { height: 40px; border-bottom: 1px solid #9ca3af; margin: 10px 0; }
  .firma-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }

  /* Piè di pagina */
  .footer { background: #f0f4f8; padding: 10px 24px; font-size: 0.75em; color: #6b7280; display: flex; justify-content: space-between; margin-top: 20px; }

  /* Print */
  @media print {
    body { font-size: 10pt; }
    .no-print { display: none !important; }
    .header { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .summary-bar { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .section-header { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    @page { margin: 1cm; }
  }
</style>
</head>
<body>

<!-- Pulsante stampa (solo schermo) -->
<div class="no-print" style="background:#1a1a2e;padding:10px 24px;display:flex;gap:10px;align-items:center">
  <button onclick="window.print()" style="background:#f97316;color:#fff;border:none;padding:8px 20px;border-radius:6px;cursor:pointer;font-weight:700;font-size:1em">
    🖨️ Salva come PDF / Stampa
  </button>
  <button onclick="window.close()" style="background:#374151;color:#fff;border:none;padding:8px 16px;border-radius:6px;cursor:pointer">
    Chiudi
  </button>
  <span style="color:#9ca3af;font-size:0.85em">Su Android: Menu → Stampa → Salva come PDF</span>
</div>

<!-- Intestazione -->
<div class="header">
  <div>
    <div class="header-title">🔥 DOS518 — VERBALE INTERVENTO AIB</div>
    <div class="header-subtitle">IRTILAC S.r.l. | Sistema Digitale Direttore Operazioni Spegnimento</div>
    <div class="header-subtitle" style="margin-top:6px;color:#60a5fa">
      DOS: <strong>${dosAttivo || '—'}</strong>
      ${oraAttivazione ? ` | Attivazione: ${oraAttivazione}` : ''}
    </div>
  </div>
  <div class="header-meta">
    <div style="font-size:2em">🔥</div>
    <div>Report generato</div>
    <div><strong>${dataReport}</strong></div>
    <div style="color:#9ca3af">${ora}</div>
  </div>
</div>

<!-- Fascia riepilogo -->
<div class="summary-bar">
  <div class="summary-item"><div class="summary-num">${totPersonale}</div><div class="summary-label">Operatori</div></div>
  <div class="summary-item"><div class="summary-num">${totMezziT}</div><div class="summary-label">Mezzi Terrestri</div></div>
  <div class="summary-item"><div class="summary-num">${totMezziA}</div><div class="summary-label">Mezzi Aerei</div></div>
  <div class="summary-item"><div class="summary-num">${totDroni}</div><div class="summary-label">Droni</div></div>
  <div class="summary-item"><div class="summary-num">${totVoci}</div><div class="summary-label">Voci Diario</div></div>
</div>

<!-- Corpo -->
<div class="body">

  <!-- COAU -->
  <div class="section">
    <div class="section-header">📋 Scheda COAU — Dati Intervento</div>
    ${coauHtml}
  </div>

  <!-- Personale -->
  <div class="section">
    <div class="section-header">
      👥 Personale Impiegato
      <span class="badge">${totPersonale} operatori</span>
    </div>
    ${personaleHtml}
  </div>

  <!-- Mezzi Terrestri -->
  <div class="section">
    <div class="section-header">
      🚒 Mezzi Terrestri
      <span class="badge">${totMezziT} mezzi</span>
    </div>
    ${mezziTHtml}
  </div>

  <!-- Mezzi Aerei -->
  <div class="section">
    <div class="section-header">
      🚁 Mezzi Aerei
      <span class="badge">${totMezziA} mezzi</span>
    </div>
    ${mezziAHtml}
  </div>

  <!-- Droni -->
  ${totDroni > 0 ? `
  <div class="section">
    <div class="section-header">
      🛸 Droni — Linea Guida 5B
      <span class="badge">${totDroni} droni</span>
    </div>
    ${droniHtml}
  </div>` : ''}

  <!-- Diario Operativo -->
  <div class="section">
    <div class="section-header">
      📓 Diario Operativo
      <span class="badge">${totVoci} eventi</span>
    </div>
    ${diarioHtml}
  </div>

  <!-- Firma DOS -->
  <div class="firma-section">
    <div class="section-header">✍️ Firme</div>
    <div class="firma-grid">
      <div class="firma-box">
        <strong>DOS — Direttore Operazioni Spegnimento</strong>
        <div class="firma-line"></div>
        <small>Nome: ${dosAttivo || '________________________'}</small>
      </div>
      <div class="firma-box">
        <strong>Responsabile Operativo (SOUP)</strong>
        <div class="firma-line"></div>
        <small>Nome: ________________________</small>
      </div>
    </div>
    <p style="margin-top:12px;font-size:0.8em;color:#6b7280">
      Documento generato da DOS518 v2025 — IRTILAC S.r.l. — Sistema conforme alla
      Linea Guida AIB n.5 CFRS Sicilia. Timestamp: ${ora}
    </p>
  </div>

</div>

<!-- Piè di pagina -->
<div class="footer">
  <span>DOS518 | IRTILAC S.r.l. | Direttore delle Operazioni di Spegnimento</span>
  <span>Linea Guida AIB n.5 · C.O.A.U. · L.A.C.E.S. · SI.TA.C.</span>
  <span>${dataReport}</span>
</div>

</body>
</html>`;
}

// ================================================================
// COMPONENTE
// ================================================================

export const ReportFinale: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const state = useAppStore.getState();
  const { coau, personale, mezziTerrestri, mezziAerei, droni, diarioOperativo, dosAttivo } = state;
  const [generando, setGenerando] = useState(false);

  const totali = {
    personale: personale.length,
    mezziT: mezziTerrestri.length,
    mezziA: mezziAerei.length,
    droni: droni.length,
    voci: (diarioOperativo || []).length,
  };

  const handleGenera = () => {
    setGenerando(true);
    const html = generaHTML(state);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 30000);
    setGenerando(false);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 10000, padding: '20px',
    }}>
      <div style={{
        background: 'linear-gradient(135deg, #0f172a, #1e293b)',
        border: '2px solid #f97316',
        borderRadius: '16px', width: '100%', maxWidth: '560px',
        boxShadow: '0 20px 60px rgba(249,115,22,0.3)',
      }}>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #7c2d12, #ea580c)',
          borderRadius: '14px 14px 0 0', padding: '20px 24px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <h2 style={{ margin: 0, color: '#fff', fontSize: '1.3em' }}>
              📄 Report Finale Intervento
            </h2>
            <small style={{ color: '#fed7aa' }}>Verbale ufficiale AIB — Linea Guida n.5</small>
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
            width: '36px', height: '36px', borderRadius: '50%', cursor: 'pointer', fontSize: '1.2rem',
          }}>✕</button>
        </div>

        <div style={{ padding: '24px' }}>

          {/* Riepilogo dati */}
          <div style={{
            background: '#0f172a', border: '1px solid #1e293b',
            borderRadius: '10px', padding: '16px', marginBottom: '20px',
          }}>
            <p style={{ color: '#94a3b8', fontSize: '0.8rem', margin: '0 0 12px' }}>
              Il report includerà i seguenti dati:
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
              {[
                { icona: '📋', label: 'COAU', valore: coau ? '✅' : '⚠️ vuoto', ok: !!coau },
                { icona: '👥', label: 'Personale', valore: `${totali.personale} op.`, ok: totali.personale > 0 },
                { icona: '🚒', label: 'Mezzi T.', valore: `${totali.mezziT}`, ok: totali.mezziT > 0 },
                { icona: '🚁', label: 'Mezzi A.', valore: `${totali.mezziA}`, ok: totali.mezziA > 0 },
                { icona: '🛸', label: 'Droni', valore: `${totali.droni}`, ok: totali.droni > 0 },
                { icona: '📓', label: 'Diario', valore: `${totali.voci} voci`, ok: totali.voci > 0 },
              ].map(({ icona, label, valore, ok }) => (
                <div key={label} style={{
                  background: '#1e293b', borderRadius: '6px', padding: '8px',
                  textAlign: 'center', border: `1px solid ${ok ? '#334155' : '#f97316'}`,
                }}>
                  <div style={{ fontSize: '1.2rem' }}>{icona}</div>
                  <div style={{ color: '#64748b', fontSize: '0.7rem' }}>{label}</div>
                  <div style={{ color: ok ? '#86efac' : '#fbbf24', fontWeight: 700, fontSize: '0.85rem' }}>{valore}</div>
                </div>
              ))}
            </div>
            {dosAttivo && (
              <p style={{ color: '#60a5fa', margin: '12px 0 0', fontSize: '0.85rem' }}>
                DOS firmatario: <strong>{dosAttivo}</strong>
              </p>
            )}
          </div>

          <p style={{ color: '#64748b', fontSize: '0.8rem', marginBottom: '20px' }}>
            Il report si apre in una nuova scheda del browser come documento HTML.
            Su Android Chrome usa <strong style={{ color: '#93c5fd' }}>Menu → Stampa → Salva come PDF</strong> per
            salvarlo. Su PC usa Ctrl+P → Salva come PDF.
          </p>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={onClose} style={{
              flex: 1, background: '#1e293b', color: '#94a3b8',
              border: '1px solid #334155', borderRadius: '8px', padding: '12px', cursor: 'pointer',
            }}>
              Annulla
            </button>
            <button
              onClick={handleGenera}
              disabled={generando}
              style={{
                flex: 2,
                background: generando ? '#334155' : 'linear-gradient(135deg, #7c2d12, #ea580c)',
                color: '#fff', border: 'none', borderRadius: '8px',
                padding: '12px', cursor: generando ? 'wait' : 'pointer',
                fontWeight: 700, fontSize: '1rem',
              }}
            >
              {generando ? '⏳ Generazione...' : '📄 Genera Report PDF'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
